import {useEffect, useMemo, useRef, useState} from 'react'
import {KTIcon} from '../../../../_metronic/helpers'
import Select from 'react-select'
import {removeIndicatorSeparator, solidSelectStyles} from '../../shared/ui/selectStyles'

export type ReportSelectOption = {
  value: number
  label: string
}

type ReportFiltersProps = {
  warehouseOptions: ReportSelectOption[]
  selectedWarehouse: ReportSelectOption
  onWarehouseChange: (option: ReportSelectOption) => void
  productOptions: ReportSelectOption[]
  selectedProducts: ReportSelectOption[]
  onProductsChange: (options: ReportSelectOption[]) => void
  onApply: () => void
}

export const ALL_WAREHOUSES_OPTION: ReportSelectOption = {
  value: 0,
  label: 'All Warehouses',
}

const ProductChips = ({products}: {products: ReportSelectOption[]}) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [visibleCount, setVisibleCount] = useState(products.length)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const calculateVisibleCount = () => {
      const availableWidth = container.offsetWidth
      if (!availableWidth) {
        setVisibleCount(products.length)
        return
      }

      let consumedWidth = 0
      let nextVisibleCount = 0
      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')
      if (context) {
        context.font = '600 12px Roboto, sans-serif'
      }

      for (let index = 0; index < products.length; index += 1) {
        const remaining = products.length - (index + 1)
        const product = products[index]
        const chipLabelWidth = context?.measureText(product.label).width || product.label.length * 7
        const chipWidth = Math.ceil(chipLabelWidth) + 34
        const moreWidth = remaining > 0 ? 74 : 0

        if (consumedWidth + chipWidth + moreWidth > availableWidth) {
          break
        }

        consumedWidth += chipWidth + 8
        nextVisibleCount += 1
      }

      setVisibleCount(nextVisibleCount || 1)
    }

    calculateVisibleCount()

    const observer = new ResizeObserver(() => calculateVisibleCount())
    observer.observe(container)

    return () => observer.disconnect()
  }, [products])

  const visibleProducts = useMemo(
    () => products.slice(0, Math.min(visibleCount, products.length)),
    [products, visibleCount]
  )
  const hiddenCount = Math.max(products.length - visibleProducts.length, 0)

  if (!products.length) {
    return <div className='text-muted fs-8 mt-2'>All Products</div>
  }

  return (
    <div ref={containerRef} className='d-flex flex-nowrap gap-2 mt-2 overflow-hidden'>
      {visibleProducts.map((product) => (
        <span
          key={product.value}
          className='badge badge-light-primary fw-semibold px-3 py-2 text-truncate'
          style={{maxWidth: '150px'}}
        >
          {product.label}
        </span>
      ))}
      {hiddenCount > 0 && (
        <span className='badge badge-light-secondary fw-semibold px-3 py-2'>{`+${hiddenCount} more`}</span>
      )}
    </div>
  )
}

export const ReportFilters = ({
  warehouseOptions,
  selectedWarehouse,
  onWarehouseChange,
  productOptions,
  selectedProducts,
  onProductsChange,
  onApply,
}: ReportFiltersProps) => {
  const [isRefreshHovered, setIsRefreshHovered] = useState(false)
  const isAllWarehousesSelected = selectedWarehouse.value === ALL_WAREHOUSES_OPTION.value
  const warehouseSelectOptions = useMemo(
    () => [ALL_WAREHOUSES_OPTION, ...warehouseOptions],
    [warehouseOptions]
  )

  useEffect(() => {
    if (
      selectedWarehouse.value !== ALL_WAREHOUSES_OPTION.value &&
      !warehouseOptions.some((option) => option.value === selectedWarehouse.value)
    ) {
      onWarehouseChange(ALL_WAREHOUSES_OPTION)
      onProductsChange([])
    }
  }, [onProductsChange, onWarehouseChange, selectedWarehouse.value, warehouseOptions])

  useEffect(() => {
    const validProductValues = new Set(productOptions.map((option) => option.value))
    const filteredProducts = selectedProducts.filter((product) => validProductValues.has(product.value))

    if (filteredProducts.length !== selectedProducts.length) {
      onProductsChange(filteredProducts)
    }
  }, [onProductsChange, productOptions, selectedProducts])

  const handleWarehouseChange = (option: ReportSelectOption | null) => {
    const nextWarehouse = option || ALL_WAREHOUSES_OPTION
    const warehouseChanged = nextWarehouse.value !== selectedWarehouse.value

    onWarehouseChange(nextWarehouse)
    if (warehouseChanged) {
      onProductsChange([])
    }
  }

  return (
    <div className='card-header border-0 pt-6'>
      <div className='card-title w-100'>
        <div className='row g-4 w-100 align-items-start'>
          <div className='col-12 col-lg-4 col-xl-3'>
            <label className='fw-bold fs-7 text-gray-600 mb-2 d-block'>Warehouse</label>
            <Select
              value={selectedWarehouse}
              onChange={(option) => handleWarehouseChange(option as ReportSelectOption | null)}
              options={warehouseSelectOptions}
              isSearchable={true}
              placeholder='All Warehouses'
              className='fw-bold'
              styles={solidSelectStyles}
              components={removeIndicatorSeparator}
              isClearable={false}
            />
          </div>

          <div className='col-12 col-lg-5 col-xl-4'>
            <label className='fw-bold fs-7 text-gray-600 mb-2 d-block'>Products</label>
            <Select
              isMulti
              value={selectedProducts}
              onChange={(options) => onProductsChange((options || []) as ReportSelectOption[])}
              options={productOptions}
              isSearchable={true}
              isDisabled={isAllWarehousesSelected}
              placeholder={
                selectedProducts.length > 0
                  ? `${selectedProducts.length} product${selectedProducts.length > 1 ? 's' : ''} selected`
                  : isAllWarehousesSelected
                  ? 'Select a warehouse first'
                  : 'All Products'
              }
              className='fw-bold'
              styles={solidSelectStyles}
              components={removeIndicatorSeparator}
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              controlShouldRenderValue={false}
              noOptionsMessage={() => 'No products found'}
            />
            <ProductChips products={selectedProducts} />
          </div>

          <div className='col-12 col-lg-2 col-xl-1 d-flex align-items-end justify-content-lg-start'>
            <button
              type='button'
              className='btn btn-lg'
              style={{
                borderRadius: '1000%',
                height: '30px',
                width: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
                marginTop: '28px',
                backgroundColor: !isRefreshHovered ? '#f0f0f0' : '#b0b0b0',
              }}
              onMouseEnter={() => setIsRefreshHovered(true)}
              onMouseLeave={() => setIsRefreshHovered(false)}
              onClick={onApply}
            >
              <KTIcon iconName='arrows-circle' className='fs-3 px-0' />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
